import { Component, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, Subject, switchMap } from 'rxjs';
import { DxTemplateModule } from 'devextreme-angular/core';
import { DxDataGridModule, DxLookupModule } from 'devextreme-angular';
import { NgIf, AsyncPipe } from '@angular/common';
import { showErrorSwal } from '@asamco/utils';
import { DxiColumnModule, DxoFilterRowModule, DxoSelectionModule } from 'devextreme-angular/ui/nested';
import { FormsModule } from '@angular/forms';
import Swal from 'sweetalert2';
import { WarningModalMessageCreateModalComponent } from './warning-modal-message-create-modal.component';
import { LinkTemplateService } from '../services/link-template.service';
import { JsonEditorModalComponent, MessageModalComponent, WarningModalMessage, WarningModalMessageService } from '@asamco/application-link/shared';

@Component({
  selector: 'app-shell-warning-modal-message-tool',
  templateUrl: './warning-modal-message-tool.component.html',
  styleUrls: ['../styles/alert.scss', '../styles/tile-style.scss', './warning-modal-message-tool.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgIf, FormsModule, DxTemplateModule, AsyncPipe, DxLookupModule, DxoSelectionModule, DxoFilterRowModule, DxiColumnModule, DxDataGridModule, JsonEditorModalComponent, WarningModalMessageCreateModalComponent, MessageModalComponent]
})
export class WarningModalMessageToolComponent implements OnDestroy, OnInit {
  @ViewChild(WarningModalMessageCreateModalComponent)
  createModal: WarningModalMessageCreateModalComponent;

  @ViewChild(MessageModalComponent, { static: false })
	modalMessage: MessageModalComponent;

  public warningModalMessages$ = new BehaviorSubject<WarningModalMessage[]>(null);

  loading = false;

  private destroy$: Subject<void> = new Subject<void>();

  constructor(private cdr: ChangeDetectorRef, private warningModalMessageService: WarningModalMessageService, private linkTemplateService: LinkTemplateService) {

  }

  ngOnInit() {
    this.linkTemplateService.getAllLinkTemplates().subscribe(lts => {
      this.createModal.selectableFromLinkTemplates = lts.filter(lt => lt.isFrom).map(lt => ({id: lt.id, label: lt.description, selected: false}));
      this.createModal.selectableToLinkTemplates = lts.filter(lt => !lt.isFrom).map(lt => ({id: lt.id, label: lt.description, selected: false}));

      this.updatePreview();
    });
  }

  onSourceSystemChange($event) {
    this.updatePreview();
  }

  updatePreview() {
    this.warningModalMessages$.next(undefined);

    this.loading = true;
      this.warningModalMessageService.getWarningModalMessages().subscribe({ next: uds => {
          for(const ud of uds) {
            if(ud.fromLinkTemplateIds) {
              const array = ud.fromLinkTemplateIds.split(',');
              ud.fromLinkTemplates = array.slice(1, array.length - 1).map(id => <any>this.createModal.selectableFromLinkTemplates.find(flt => flt.id === +id) || {id: id, label: id});
            } else {
              ud.fromLinkTemplates = [];
            }
            if(ud.toLinkTemplateIds) {
              const array = ud.toLinkTemplateIds.split(',');
              ud.toLinkTemplates = array.slice(1, array.length - 1).map(id => <any>this.createModal.selectableToLinkTemplates.find(flt => flt.id === +id) || {id: id, label: id});
            } else {
              ud.toLinkTemplates = [];
            }
          }
          this.loading = false;
          this.warningModalMessages$.next(uds);
        }, error: showErrorSwal });
  }

  delete(id: number) {
      this.loading = true;

      this.warningModalMessageService.deleteWarningModalMessage(id).subscribe({
        next: r => {
          this.loading = false;

          Swal.fire({
            icon: 'success',
            title: `Message successfully deleted.`
          });
        }, error: showErrorSwal, complete: () => {
          this.loading = false;
          this.updatePreview();
        }
      });
  }

  update(message: WarningModalMessage) {
    this.createModal.show(message).pipe(switchMap(model => {
        this.loading = true;
        this.warningModalMessages$.next(undefined);
        return this.warningModalMessageService.updateWarningModalMessage(model);
      })).subscribe({next: r => {
      Swal.fire({
            icon: 'success',
            title: `Warning modal message updated.`
      });
      this.updatePreview();
    }, error: showErrorSwal});
  }

  create() {
    this.createModal.show().pipe(switchMap(model => {
        this.loading = true;
        this.warningModalMessages$.next(undefined);
        return this.warningModalMessageService.createWarningModalMessage(model);
      })).subscribe({next: r => {
      Swal.fire({
            icon: 'success',
            title: `Warning modal message created.`
      });
      this.updatePreview();
    }, error: showErrorSwal});
  }

  view(data: WarningModalMessage) {
    this.modalMessage.showForModalMessage(data);
  }

  fromLinkTemplateCellValue(data: WarningModalMessage) {
    return data.fromLinkTemplates.map(flt => flt.label).join(', ');
  }

  toLinkTemplateCellValue(data: WarningModalMessage) {
    return data.toLinkTemplates.map(tlt => tlt.label).join(', ');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
